import React, { useState, useEffect, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { MessageSquare, ArrowLeft, Video } from 'lucide-react';
import axios from 'axios';
import { useSiteTranslation } from '../hooks/useSiteTranslation';
import TestimonialCard from '../components/Testimonials/TestimonialCard';
import TestimonialForm from '../components/Testimonials/TestimonialForm';
import type { PublicTestimonial } from '../components/Testimonials/types';

type Filter = 'all' | 'video';

const Testimonials: React.FC = () => {
  const { ts, t } = useSiteTranslation();
  const [items, setItems] = useState<PublicTestimonial[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<Filter>('all');
  const [searchParams] = useSearchParams();
  // ?enviar=1 → link direto (menu, WhatsApp/e-mail da equipe) já abre o formulário
  const [showForm, setShowForm] = useState(searchParams.get('enviar') === '1');
  const [sent, setSent] = useState(false);
  const formRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (searchParams.get('enviar') !== '1') return;
    // Depois do ScrollToTop da troca de rota
    const id = window.setTimeout(() => formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
    return () => window.clearTimeout(id);
  }, [searchParams]);

  useEffect(() => {
    setLoading(true);
    axios.get(`/testimonials?limit=100${filter === 'video' ? '&hasVideo=1' : ''}`)
      .then((res) => setItems(res.data))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [filter]);

  const filters: { key: Filter; label: string; icon?: React.ReactNode }[] = [
    { key: 'all', label: t('testimonialsUi.filterAll') },
    { key: 'video', label: t('testimonialsUi.filterVideo'), icon: <Video className="w-4 h-4" /> },
  ];

  return (
    <div className="min-h-screen">
      <section className="section-padding bg-gray-50">
        <div className="container-custom">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-gray-600 hover:text-primary-600 mb-8 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            {t('testimonials.backHome')}
          </Link>

          <div className="text-center mb-16">
            <h1 className="text-4xl font-bold text-gray-900 mb-6">{ts('testimonials.title')}</h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">{ts('testimonials.subtitle')}</p>
          </div>

          <div ref={formRef} className="mb-12 flex justify-center scroll-mt-24">
            {!showForm ? (
              <div className="flex flex-col items-center gap-4">
                {sent && (
                  <div className="p-3 bg-green-50 text-green-800 rounded-lg text-sm max-w-xl" role="status">
                    {t('testimonialsUi.success')}
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => { setShowForm(true); setSent(false); }}
                  className="btn-primary inline-flex items-center gap-2"
                >
                  <MessageSquare className="w-5 h-5" />
                  {t('testimonialsUi.leaveCta')}
                </button>
              </div>
            ) : (
              <TestimonialForm
                onCancel={() => setShowForm(false)}
                onSuccess={() => { setShowForm(false); setSent(true); }}
              />
            )}
          </div>

          <div className="flex justify-center gap-2 mb-10" role="tablist">
            {filters.map((f) => (
              <button
                key={f.key}
                type="button"
                role="tab"
                aria-selected={filter === f.key}
                onClick={() => setFilter(f.key)}
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  filter === f.key
                    ? 'bg-primary-600 text-white shadow'
                    : 'bg-white text-gray-700 border border-gray-200 hover:border-primary-300'
                }`}
              >
                {f.icon}
                {f.label}
              </button>
            ))}
          </div>

          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600" />
            </div>
          ) : items.length === 0 ? (
            <div className="text-center py-16 px-4 rounded-xl bg-white border border-gray-200">
              <MessageSquare className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-600 mb-4">{filter === 'video' ? t('testimonialsUi.emptyVideo') : t('testimonials.empty')}</p>
              <Link to="/" className="btn-primary inline-flex items-center gap-2">
                <ArrowLeft className="w-4 h-4" />
                {t('testimonials.backHome')}
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {items.map((item) => (
                <TestimonialCard key={item._id} item={item} />
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Testimonials;
